// Parse multi-state slash-delimited values into individual state tokens
export function parseStateTokens(stateStr) {
  if (!stateStr) return [];
  return stateStr.split('/').map(s => s.trim()).filter(Boolean);
}

// Get all unique base states from an array of stations
export function getAllBaseStates(stations) {
  const states = new Set();
  stations.forEach(s => {
    parseStateTokens(s.state).forEach(st => states.add(st));
  });
  return Array.from(states).sort();
}

// Get distinct values for a field
export function getDistinctValues(stations, field) {
  const values = new Set();
  stations.forEach(s => {
    if (s[field]) values.add(s[field]);
  });
  return Array.from(values).sort();
}

// Count by a field, return sorted array of {label, count}
export function countByField(stations, field, limit = 6) {
  const counts = {};
  stations.forEach(s => {
    const val = s[field] || 'Unknown';
    counts[val] = (counts[val] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

// Smart state match — checks if any token in the row's state matches the filter
export function stateMatchesFilter(rowState, filterState) {
  if (!filterState) return true;
  const tokens = parseStateTokens(rowState);
  return tokens.includes(filterState);
}

// Check if a station matches the search query
export function stationMatchesSearch(station, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    (station.state || '').toLowerCase().includes(q) ||
    (station.market || '').toLowerCase().includes(q) ||
    (station.station || '').toLowerCase().includes(q) ||
    (station.affiliate || '').toLowerCase().includes(q)
  );
}

// Compute field changes between old and new record
export function computeFieldChanges(oldRecord, newRecord, fields) {
  const changes = [];
  fields.forEach(field => {
    const oldVal = (oldRecord[field] || '').toString();
    const newVal = (newRecord[field] || '').toString();
    if (oldVal !== newVal) {
      changes.push({ field, old_value: oldVal, new_value: newVal });
    }
  });
  return changes;
}

export const STATION_FIELDS = [
  'state', 'market', 'station', 'affiliate', 'rep_firm', 'rep',
  'rep_phone', 'rep_email', 'ownership', 'cd_contact_name',
  'cd_contact_title', 'cd_contact_phone', 'cd_contact_email',
  'station_address', 'station_city', 'station_state', 'station_zip'
];

export const MEDIA_TYPES = [
  'Broadcast', 'Cable', 'Radio',
  'Addressable Satellite', 'Regional Sports Network'
];

export const MANAGER_FIELDS = [
  'media_type', 'rep_firm', 'manager', 'title', 'email',
  'office_phone', 'contact_mobile'
];

export const KNOWN_REP_FIRMS = [
  'KATZ', 'LOCALITY', 'NEXSTAR', 'GRAY', 'TEGNA', 'SCRIPPS', 'NBC',
  'HEARST', 'FOX', 'CBS', 'ABC', 'LOCAL', 'DIRECT', 'KTV MEDIA', 'PINNACLE'
];

// Manager firm name normalization for imports
const FIRM_NORMALIZATIONS = {
  'GRAY TV': 'GRAY',
  'GRAY TELEVISION': 'GRAY',
  'NBC / TELEMUNDO': 'NBC',
  'NBC/TELEMUNDO': 'NBC',
  'ABC (DISNEY ADVERTISING SALES)': 'ABC',
  'ABC DISNEY': 'ABC',
  'DISNEY ADVERTISING SALES': 'ABC',
};

export function normalizeManagerFirmName(name) {
  if (!name) return '';
  const upper = name.trim().toUpperCase();
  return FIRM_NORMALIZATIONS[upper] || name.trim();
}